import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, Menu, X, ArrowLeft, Shield } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import api from '../../lib/api';

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function Topbar({ onMenuClick }) {
  const navigate = useNavigate();
  const { user, organization } = useAuth();
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef(null);
  const queryClient = useQueryClient();
  const isMasterAdmin = user?.isMasterAdmin;
  const viewingOrgAsAdmin = localStorage.getItem('viewingOrgAsAdmin');

  const handleBackToSelector = () => {
    localStorage.removeItem('viewingOrgAsAdmin');
    navigate('/org-selector');
  };

  const pageTitle = (() => {
    const path = window.location.pathname;
    const map = {
      '/dashboard': 'Dashboard',
      '/team': 'Team',
      '/projects': 'Projects',
      '/timelogs': 'Time Logs',
      '/screenshots': 'Screenshots',
      '/reports': 'Reports',
      '/invoices': 'Invoices',
      '/settings': 'Settings',
      '/work-diary': 'Work Diary',
    };
    return map[path] || import.meta.env.VITE_APP_NAME || 'TrackFlow';
  })();

  const { data: unreadData } = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: async () => {
      const res = await api.get('/api/notifications/unread');
      return res.data.data;
    },
    refetchInterval: 60000,
    staleTime: 30000,
  });

  const markReadMutation = useMutation({
    mutationFn: () => api.post('/api/notifications/mark-read'),
    onSuccess: () => queryClient.setQueryData(['notifications-unread'], { count: 0, notifications: [] }),
  });

  const unreadCount = unreadData?.count || 0;
  const notifications = unreadData?.notifications || [];

  // Close dropdown on outside click
  useEffect(() => {
    if (!bellOpen) return;
    function handleClick(e) {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setBellOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [bellOpen]);

  function handleBellClick() {
    if (!bellOpen && unreadCount > 0) {
      markReadMutation.mutate();
    }
    setBellOpen((v) => !v);
  }

  return (
    <header className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
      <div className="flex items-center gap-3 flex-1">
        {isMasterAdmin && viewingOrgAsAdmin && (
          <button
            onClick={handleBackToSelector}
            className="p-2 rounded-md text-blue-600 hover:bg-blue-50 transition-colors"
            title="Back to organization selector"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        {onMenuClick && (
          <button
            onClick={onMenuClick}
            className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors lg:hidden"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}
        <div className="flex flex-col">
          <h1 className="text-xl font-semibold text-gray-900">{pageTitle}</h1>
          {organization && (
            <div className="flex items-center gap-2">
              <p className="text-xs text-gray-500">{organization.name}</p>
              {isMasterAdmin && viewingOrgAsAdmin && (
                <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-[11px] font-medium">
                  <Shield className="w-3 h-3" />
                  Admin View
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Bell with dropdown */}
        <div className="relative" ref={bellRef}>
          <button
            onClick={handleBellClick}
            className="relative p-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 flex items-center justify-center w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {bellOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-900">Notifications</span>
                <button
                  onClick={() => setBellOpen(false)}
                  className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">
                  No new notifications
                </div>
              ) : (
                <ul className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                  {notifications.map((n) => (
                    <li key={n.id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                      {n.url ? (
                        <a
                          href={n.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                          onClick={() => setBellOpen(false)}
                        >
                          <p className="text-sm font-medium text-gray-900">{n.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                          <p className="text-xs text-gray-400 mt-1">{timeAgo(n.createdAt)}</p>
                        </a>
                      ) : (
                        <div>
                          <p className="text-sm font-medium text-gray-900">{n.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                          <p className="text-xs text-gray-400 mt-1">{timeAgo(n.createdAt)}</p>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full text-white text-sm font-semibold">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="hidden sm:block">
            <p className="text-sm font-medium text-gray-900">{user?.name || 'User'}</p>
            <p className="text-xs text-gray-500">{user?.email || ''}</p>
          </div>
        </div>
      </div>
    </header>
  );
}
